/**
 * Zod schemas + DTO types for the Workload API surface — shared between
 * API (validation) and Web (form state + types). Phase 3 (W1.T2).
 *
 * Coverage:
 *   - (Workload PLAN write paths — СНЯТЫ: схемы запросов плана убраны
 *     волной Ф4 · В10-А2, таблица плана снесена Ф7 · Р5, Р-133.)
 *   - Workload FACT write paths: create / update.
 *   - Read queries: grid + heatmap + cell changelog.
 *   - Response envelopes: grid, heatmap, cell.
 *
 * ────────────────────────────────────────────────────────────────────────
 * INVARIANTS (gate 1 / D01 / OQ-W9):
 *   - hours: number, > 0, <= 24, multiple of 0.5 (i.e. 0.5, 1, 1.5, …, 24).
 *     Hard-zero rejects (use DELETE to remove a row, not 0-hours upsert).
 *   - comment / reportText: max 2000 UTF-16 chars (mirrors Postgres CHECK).
 *   - date strings: YYYY-MM-DD, syntactic only — calendar validity is
 *     re-checked server-side against `today_msk` (Europe/Moscow). FE schemas
 *     deliberately do NOT call `new Date(s)` because TZ semantics drift.
 *   - period span: hard-cap 92 days (asserted server-side; FE schemas keep
 *     the syntactic bound to keep error surface narrow — if FE allows a
 *     wider window the BE will return 422).
 *   - Every mutating request uses `.strict()` so a smuggled extra key (e.g.
 *     `employeeId` on a self-FACT write) is rejected as VALIDATION_ERROR.
 * ────────────────────────────────────────────────────────────────────────
 */
import { z } from 'zod';
import { ruEnum } from './lib/zod-ru';
// Календарная годность даты — ОДНО правило на пакет (долг #25, разбор у
// `WorkloadDateSchema`). Направление зависимости `workload → work`, обратной
// нет: цикла не возникает.
import { isCalendarDate } from './work';

// ─── Primitive shapes ──────────────────────────────────────────────────────

/**
 * Строка `YYYY-MM-DD`, СУЩЕСТВУЮЩАЯ в календаре.
 *
 * ─── ПРЕЖНЯЯ ФОРМУЛИРОВКА СНЯТА, И ЕЁ ДОВОД ОКАЗАЛСЯ НЕВЕРЕН (долг #25) ────
 *
 * Здесь стояло «синтаксис — да, календарь — нет; годность даты доверена
 * Postgres через приведение `$1::date`». Довод держится только на ЗАПИСИ: у
 * неё действительно есть приведение, и `2026-02-31` отвергает база. Но те же
 * даты приходят и в ЧТЕНИЕ — `periodFrom` / `periodTo` тепловой карты и
 * графика занятости, — а там до Postgres дело не доходит вовсе:
 * `parseIsoDateUtc` (`apps/api/src/common/date/today-msk.ts:51-53`) делает
 * `new Date('2026-13-45T00:00:00Z')`, получает `Invalid Date`, и падение
 * случается НИЖЕ по стеку, уже 500-м. То есть отказ выглядел как поломка
 * сервера, а не как негодный запрос.
 *
 * ⚠️ МАРШРУТА ДВА, ДЕФЕКТ ОДИН. `GET /workload/heatmap` и `GET /workload/gantt`
 * разбирают дату ОДНИМ И ТЕМ ЖЕ способом (`workload-gantt.service.ts:98-99`),
 * поэтому и правка одна — здесь, в общей схеме. Чинить её в сервисе значило бы
 * завести две проверки, которые разойдутся на третьем маршруте.
 *
 * Существование проверяется обратным преобразованием, а не таблицей длин
 * месяцев: `Date.parse('2026-02-31T00:00:00Z')` молча «чинит» дату в 3 марта,
 * и сравнение исходной строки с обратно отформатированной ловит именно это.
 * Правило ОДНО на весь монорепозиторий — `isCalendarDate` из `./work`
 * (заведена волной В3a); вторая копия здесь разошлась бы с ней тихо.
 */
export const WorkloadDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, {
    message: 'Дата должна быть в формате ГГГГ-ММ-ДД',
  })
  .refine(isCalendarDate, {
    message: 'Такой даты не существует в календаре',
  });
export type WorkloadDate = z.infer<typeof WorkloadDateSchema>;

/**
 * Hours value: > 0, <= 24, multiple of 0.5. Mirrors Postgres CHECK
 * `hours > 0 AND hours <= 24 AND (hours * 2) = floor(hours * 2)`.
 *
 * Edge cases the refine catches: 0, 24.5, 0.4, NaN, Infinity, -1.
 */
export const WorkloadHoursSchema = z
  .number()
  .refine((v) => Number.isFinite(v) && v > 0 && v <= 24 && (v * 2) % 1 === 0, {
    message: 'Часы должны быть > 0, ≤ 24 и кратны 0.5',
  });
export type WorkloadHours = z.infer<typeof WorkloadHoursSchema>;

/** Free-text user-authored field — max 2000 chars (gate 1). */
export const WorkloadCommentSchema = z.string().max(2000, {
  message: 'Комментарий не должен превышать 2000 символов',
});

/** UUID — narrowed to v4 string but accepts any RFC-4122 form. */
const UuidSchema = z.string().uuid();

/** Cell entity discriminator — used for changelog query + response. */
export const WorkloadEntityKindSchema = z.enum(['PLAN', 'FACT'], ruEnum('Недопустимый вид записи'));
export type WorkloadEntityKind = z.infer<typeof WorkloadEntityKindSchema>;

/** Subject of a workload grid request (M1: only `employee`). */
export const WorkloadSubjectSchema = z.enum(['employee'], ruEnum('Недопустимый субъект'));
export type WorkloadSubject = z.infer<typeof WorkloadSubjectSchema>;

// ─── Plan write requests — СНЯТЫ волной Ф4 · В10-А2 (Р-25, Р-26) ──────────
//
// Здесь жили `CreatePlanRequestSchema`, `UpdatePlanRequestSchema`,
// `BulkPlanRowSchema` и `BulkUpsertPlanRequestSchema` — тела четырёх
// плановых маршрутов. Ручной ввод плановой загрузки отменён решением
// владельца Р-25: календарная сетка становится сеткой ФАКТА, плановая
// сторона работы выражается полосой на графике занятости.
//
// ⚠️ `WorkloadEntityKindSchema` (`['PLAN','FACT']`) НАМЕРЕННО НЕ СУЖЕН.
// Перечисление общее у журнала изменений и у формы ячейки
// (`WorkloadCellResponse.kind`), а строки аудита `workload_plan` остаются
// навсегда (сама таблица `WorkloadPlan` снесена Ф7 · Р5, Р-133 — довод
// «строки в базе» отпал, довод «общий носитель» держит перечисление один).
// Отказ по виду `PLAN` живёт на сервисе (`workload.service.getChangelog`),
// где предмет узкий.

// ─── Fact write requests ──────────────────────────────────────────────────

/**
 * `POST /workload/fact` body. Self-only — `employeeId` is taken from JWT,
 * NOT accepted in the body (defense against a Manager smuggling another
 * employee's id; D04).
 */
export const CreateFactRequestSchema = z
  .object({
    projectId: UuidSchema,
    date: WorkloadDateSchema,
    hours: WorkloadHoursSchema,
    reportText: WorkloadCommentSchema.optional(),
    /**
     * Block 2 «Трекер объектов» Этап 01a (KD-9 → A). Optional «раздел объекта».
     * ADDITIVE — `.strict()` still rejects any OTHER extra key. The service
     * (W2 T2.1) verifies the section belongs to the project; the DB compound FK
     * is the last line.
     *
     * ⚠️ Ф5 · В9.7 (Р-97) — «`null` (или отсутствие) = «Прочее»» БОЛЬШЕ НЕ
     * ВЕРНО, и прежняя формулировка снята именно поэтому. Пустой раздел сам по
     * себе законен (час может быть привязан к РАБОТЕ), но пустой ОДНОВРЕМЕННО
     * с пустым `workId` — нет: сервер отвечает 422
     * `WORK_OR_SECTION_REQUIRED`. Час всегда привязан либо к работе, либо к
     * виду внутренней работы.
     */
    sectionId: UuidSchema.nullable().optional(),
    /**
     * Блок 2 · Ф2 · В2c — работа, к которой относится факт. РАСШИРЕНИЕ схемы
     * «расширить и сжать»: новый путь записи появился, старый работал рядом.
     *
     * ✅ СЖАТИЕ СОСТОЯЛОСЬ — Ф5 · В9.7, решение Р-97. Обещание «сжатие делает
     * волна-переключатель В9.7» ИСПОЛНЕНО: старый путь (час без работы и без
     * раздела) снят. Пункт «Без работы» убран из интерфейса волнами В9-b (бот)
     * и В9-b.1 (веб), а возможность записать такую строку — этой.
     *
     * ⚠️ ПОЛЕ ПРИ ЭТОМ ОСТАЛОСЬ `nullable().optional()`, И ЭТО НЕ НЕДОДЕЛКА.
     * Обязательна ПРИВЯЗКА, а не `workId`: час на служебном объекте ложится
     * ПРЯМО НА ВИД (Р-90), и `workId` там пуст ЗАКОННО. Требование связывает
     * ПАРУ полей — «хотя бы одно», — а Zod-объект такому условию не место:
     * выражение пары в схеме дало бы обобщённый 400 вместо именованного 422 и
     * развело бы один рубеж по двум слоям с двумя формами ответа. Рубеж живёт
     * в сервисе (`workload.service.assertFactAttachment`, 422
     * `WORK_OR_SECTION_REQUIRED`) — там же, где и его близнец
     * `SECTION_COMES_FROM_WORK`, устроенный ровно так же и по той же причине.
     *
     * ⚠️ ВМЕСТЕ С `sectionId` НЕ ПРИСЫЛАЕТСЯ. При заданной работе раздел берётся
     * ИЗ НЕЁ и из запроса не принимается — сервис отвечает 422, а не молча
     * выбирает один из двух источников. Молчаливый выбор дал бы факт, чей
     * раздел не совпадает с разделом собственной работы, и заметить это было бы
     * нечем.
     */
    workId: UuidSchema.nullable().optional(),
  })
  .strict();
export type CreateFactRequest = z.infer<typeof CreateFactRequestSchema>;

/** `PATCH /workload/fact/:id` — same self-edit-only constraint at service. */
export const UpdateFactRequestSchema = z
  .object({
    version: z.number().int().nonnegative(),
    hours: WorkloadHoursSchema.optional(),
    reportText: WorkloadCommentSchema.optional(),
    /**
     * Block 2 Этап 01a — re-target the fact's «раздел». Additive.
     *
     * ⚠️ Ф5 · В9.7 (Р-97) — «(null = «Прочее»)» СНЯТО. Обнулить раздел можно
     * только пока у факта остаётся работа: правка, после которой у строки не
     * остаётся НИ работы, НИ раздела, отвергается 422
     * `WORK_OR_SECTION_REQUIRED`. Правка, привязки НЕ несущая (одни часы),
     * рубеж не зовёт вовсе — унаследованные строки без привязки остаются
     * правимыми навсегда (граница Р-97).
     */
    sectionId: UuidSchema.nullable().optional(),
    /**
     * Блок 2 · Ф2 · В2c — работа, к которой относится факт. РАСШИРЕНИЕ схемы
     * «расширить и сжать»: новый путь записи появился, старый работал рядом.
     *
     * ✅ СЖАТИЕ СОСТОЯЛОСЬ — Ф5 · В9.7, решение Р-97. Обещание «сжатие делает
     * волна-переключатель В9.7» ИСПОЛНЕНО: старый путь (час без работы и без
     * раздела) снят. Пункт «Без работы» убран из интерфейса волнами В9-b (бот)
     * и В9-b.1 (веб), а возможность записать такую строку — этой.
     *
     * ⚠️ ПОЛЕ ПРИ ЭТОМ ОСТАЛОСЬ `nullable().optional()`, И ЭТО НЕ НЕДОДЕЛКА.
     * Обязательна ПРИВЯЗКА, а не `workId`: час на служебном объекте ложится
     * ПРЯМО НА ВИД (Р-90), и `workId` там пуст ЗАКОННО. Требование связывает
     * ПАРУ полей — «хотя бы одно», — а Zod-объект такому условию не место:
     * выражение пары в схеме дало бы обобщённый 400 вместо именованного 422 и
     * развело бы один рубеж по двум слоям с двумя формами ответа. Рубеж живёт
     * в сервисе (`workload.service.assertFactAttachment`, 422
     * `WORK_OR_SECTION_REQUIRED`) — там же, где и его близнец
     * `SECTION_COMES_FROM_WORK`, устроенный ровно так же и по той же причине.
     *
     * ⚠️ ВМЕСТЕ С `sectionId` НЕ ПРИСЫЛАЕТСЯ. При заданной работе раздел берётся
     * ИЗ НЕЁ и из запроса не принимается — сервис отвечает 422, а не молча
     * выбирает один из двух источников. Молчаливый выбор дал бы факт, чей
     * раздел не совпадает с разделом собственной работы, и заметить это было бы
     * нечем.
     */
    workId: UuidSchema.nullable().optional(),
  })
  .strict();
export type UpdateFactRequest = z.infer<typeof UpdateFactRequestSchema>;

// ─── Read queries ────────────────────────────────────────────────────────

/**
 * Ф8 · В-Н — МЕСТО, из которого запрошена таблица часов (`GET /workload/grid`).
 *
 * Один и тот же маршрут зовут три места веба:
 *
 *   · `workload` — страница «Загруженность». То же означает ОТСУТСТВИЕ
 *     признака;
 *   · `dashboard` — недельное окно сотрудника на дашборде. Период запроса —
 *     не длиннее {@link WORKLOAD_GRID_DASHBOARD_MAX_DAYS} дней;
 *   · `project` — окно отчётов сотрудника в карточке объекта. Запрос обязан
 *     нести `projectId`.
 *
 * ⚠️ Ф8 · В-С — ПРИЗНАК НА ДОСТУП БОЛЬШЕ НЕ ВЛИЯЕТ. Он заводился, чтобы окна
 * вне страницы не подчинялись настройке `WORKLOAD_VIEW_LEVEL`: её значение
 * `employee` сужало руководителя отдела и администратора на странице до
 * «только сам». Значение упразднено решением владельца 11.10.2026, настройку
 * сервер на маршрутах не читает, и при любом признаке отвечает один и тот же
 * разбор — право № 7 и должность.
 *
 * Что осталось: запрос с признаком обязан иметь ФОРМУ своего окна (объект
 * либо недельный период) — иначе 422. Охранять этой проверке сегодня нечего:
 * запрос, который она отвергает, без признака проходит. Признак и проверка
 * оставлены как договор с вебом; вырезать их — отдельное решение.
 */
export const WORKLOAD_GRID_PLACES = ['workload', 'dashboard', 'project'] as const;
export const WorkloadGridPlaceSchema = z.enum(
  WORKLOAD_GRID_PLACES,
  ruEnum('Недопустимое место запроса таблицы часов'),
);
export type WorkloadGridPlace = z.infer<typeof WorkloadGridPlaceSchema>;

/**
 * Страница ли «Загруженность» — место этого запроса.
 *
 * Отсутствие признака — «да».
 *
 * ⚠️ Ф8 · В-С: в коде сервера и веба помощник больше НЕ ЗОВЁТСЯ — развилка
 * «страница / окно», ради которой он заведён, снята вместе со значением
 * `employee` настройки `WORKLOAD_VIEW_LEVEL`. Оставлен вместе с признаком.
 */
export function isWorkloadPagePlace(place: WorkloadGridPlace | undefined): boolean {
  return place === undefined || place === 'workload';
}

/** Сколько дней, включая оба края, несёт запрос недельного окна дашборда. */
export const WORKLOAD_GRID_DASHBOARD_MAX_DAYS = 7;

/**
 * `GET /workload/grid` query. Single-employee grid view (M1 only supports
 * `subject === 'employee'`). Period range capped at 92 days server-side.
 */
export const WorkloadGridQuerySchema = z
  .object({
    subject: WorkloadSubjectSchema.optional().default('employee'),
    employeeId: UuidSchema,
    periodFrom: WorkloadDateSchema,
    periodTo: WorkloadDateSchema,
    /** Optional project filter — narrows visible rows. */
    projectId: UuidSchema.optional(),
    /**
     * Ф8 · В-Н — место запроса; разбор — у {@link WorkloadGridPlaceSchema}.
     *
     * ⚠️ `optional()`, А НЕ `default('workload')`. С умолчанием поле стало бы
     * обязательным в выведенном типе, и каждый вызов веба перестал бы
     * собираться. Отсутствие читает сервер — как «страница».
     */
    place: WorkloadGridPlaceSchema.optional(),
  })
  .strict();
export type WorkloadGridQuery = z.infer<typeof WorkloadGridQuerySchema>;

/**
 * `GET /workload/heatmap` query. Department-scoped overload view (D11);
 * absence days are excluded from the bucket calc.
 *
 * ⚠️ Р-55 (19.08.2026) — КАРТА ПРИНИМАЕТ ОТБОР ПО СОТРУДНИКУ, и это новое
 * поле схемы. Прежняя редакция `employeeId` не имела вовсе, а контроллер
 * вырезал его из запроса ДО разбора (разбор — там же, у маршрута). Отбор по
 * человеку сужает карту до ОДНОЙ строки; человек вне когорты (Р-38 — строками
 * бывают только EMPLOYEE и MANAGER) даёт ПУСТУЮ карту, а не отказ: сужение
 * ANDится с уже решёнными границами отдела и объекта, а не заменяет их.
 */
export const WorkloadHeatmapQuerySchema = z
  .object({
    departmentId: z.coerce.number().int().positive().optional(),
    periodFrom: WorkloadDateSchema,
    periodTo: WorkloadDateSchema,
    projectId: UuidSchema.optional(),
    /** Р-55 — сужение до одного человека. `undefined` — вся когорта. */
    employeeId: UuidSchema.optional(),
  })
  .strict();
export type WorkloadHeatmapQuery = z.infer<typeof WorkloadHeatmapQuerySchema>;

/**
 * `GET /workload/changelog` query. Lazy-loaded by the cell history panel.
 * `entityIds` is a comma-separated list (kept as string here — service
 * splits + validates each as UUID).
 */
export const WorkloadCellChangelogQuerySchema = z
  .object({
    entity: WorkloadEntityKindSchema,
    entityIds: z.string().min(1).max(2048),
    /** Cursor (opaque base64(JSON({ at: ISO, id }))) — see audit_log pagination. */
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  })
  .strict();
export type WorkloadCellChangelogQuery = z.infer<typeof WorkloadCellChangelogQuerySchema>;

// ─── Response envelopes ─────────────────────────────────────────────────

/**
 * A single rendered cell.
 *
 * ⚠️ С волной Ф4 · В10-А2 сетка отдаёт ТОЛЬКО `kind: 'FACT'` — плановую
 * выборку `getGrid` больше не делает (Р-26). Само поле `kind` и его
 * перечисление остаются: форма ячейки — общий носитель, и сужать его ради
 * снятой половины значило бы задеть журнал изменений заодно. (Таблица
 * `WorkloadPlan` снесена Ф7 · Р5, Р-133; прежний довод «строки живут в
 * базе» с этого релиза неверен.)
 *
 * Field semantics:
 *   - `comment` — заметка руководителя на строке (e.g. "Согласовали ТЗ").
 *     Stored in `WorkloadFact.comment`.
 *   - `reportText` — FACT-only narrative (the text an employee dictated
 *     or typed describing what they actually did). Stored in
 *     `WorkloadFact.reportText`. Optional in
 *     the schema for forward-compat with stale BE images during a
 *     rollout — pre-fix-pack-G servers omit the field entirely.
 *     (Post-deploy fix-pack cluster G, 2026-05-10. Before this fix
 *     `WorkloadFact.reportText` was stored on write but never returned
 *     on read, so the «отчет сотрудника» surface was empty.)
 */
export const WorkloadCellResponseSchema = z.object({
  id: UuidSchema,
  date: WorkloadDateSchema,
  employeeId: UuidSchema,
  projectId: UuidSchema,
  hours: z.number(),
  comment: z.string().nullable(),
  reportText: z.string().nullable().optional(),
  /**
   * Block 2 «Трекер объектов» Этап 01a. The «раздел» this FACT is attributed
   * to (`null` = «Прочее»). Strictly additive — stale FE images ignore it,
   * pre-phase BE images omit it. `sectionName` is a convenience join (may be absent when
   * the read path does not include the section — callers tolerate `?? null`).
   * The grid wiring is W2 (T2.2a); this is the response contract only.
   */
  sectionId: z.string().nullable().optional(),
  sectionName: z.string().nullable().optional(),
  /**
   * Ф2 · В2c — работа, к которой привязан факт (`null` = привязки нет).
   * Строго аддитивно: устаревший клиент поле игнорирует.
   */
  workId: z.string().nullable().optional(),
  version: z.number().int().nonnegative(),
  kind: WorkloadEntityKindSchema,
});
export type WorkloadCellResponse = z.infer<typeof WorkloadCellResponseSchema>;

/** A "day column" descriptor in the grid header. */
export const WorkloadGridDaySchema = z.object({
  date: WorkloadDateSchema,
  isWeekend: z.boolean(),
  isToday: z.boolean(),
  /** D11 — true if employee has an active Absence on this day. */
  isAbsence: z.boolean().optional(),
  absenceKind: z.enum(['VACATION', 'SICK_LEAVE']).nullable().optional(),
});
export type WorkloadGridDay = z.infer<typeof WorkloadGridDaySchema>;

/**
 * Block 2 «Трекер объектов» Этап 01a (T2.2b). A single «раздел объекта» carried
 * on a project row so the FE can render section sub-rows. `id` is a section
 * UUID; `name` is the snapshot display name; `sortOrder` is the manager-defined
 * ordering (mirrors the mgmt tab / T2.5) so «Загрузка» and the management view
 * agree on section order. The array is delivered PRE-SORTED by
 * `(sortOrder asc, name ru, id)`; `sortOrder` is carried explicitly so the FE
 * can re-sort or key on it without a second fetch. «Прочее» (the NULL-section
 * bucket) is NOT a member of this array — it has no section id — the FE derives
 * it from `cells[].sectionId === null`. `id` kept lenient (`z.string()`, not a
 * strict uuid) to mirror the cell `sectionId` field and stay forward-compatible.
 */
export const WorkloadGridSectionSchema = z.object({
  id: z.string(),
  name: z.string(),
  sortOrder: z.number().int(),
});
export type WorkloadGridSection = z.infer<typeof WorkloadGridSectionSchema>;

/** A "row" descriptor — one employee or one project (subject-dependent). */
export const WorkloadGridRowSchema = z.object({
  id: UuidSchema,
  label: z.string(),
  /** Optional project code (e.g. for project-subject rows in heatmap). */
  code: z.string().optional(),
  /** Department slug, present for employee rows. */
  departmentId: z.number().int().nullable().optional(),
  /**
   * Phase 3.1 (R2 — archived project union). Set to `true` when this row
   * represents an archived project that appears in the calendar via the
   * period-union arm of `getGrid` (i.e. the employee has FACT rows on this
   * project in the requested period, but no active participation — рука
   * объединения стала фактовой с волны Ф4 · В10-А2).
   *
   * ⚠️ ПОПРАВКА 19.08.2026 — ИСТОЧНИК ТАКОЙ СТРОКИ БОЛЬШЕ НЕ ОДИН. Кроме
   * фактов периода объединение несёт ОБЪЕКТЫ РАБОТ субъекта (`getGrid`,
   * выборка `assignedWorkProjects`): человек с задачей на объекте, но без
   * участия и без внесённых часов, прежде не видел объект вовсе. Рубеж прав у
   * архивных строк ОБЩИЙ и не изменился — `projectVisibilityFilter(actor)`, —
   * поэтому «архивный и чужой» сюда по-прежнему не попадает.
   *
   * The FE renders an "Архивирован" badge and treats the row as read-only —
   * no cell-write affordance. ⚠️ Прежде довод был «плановые записи на
   * архивных объектах всё равно блокирует сервер (каскад D02)». Ручной
   * плановой записи не существует вовсе с волны Ф4 · В10-А2 (Р-26), а
   * фактовые записи на архивном объекте сервер блокирует сам.
   *
   * Truly optional (no zod default) so the inferred TS type keeps the
   * field optional — heatmap rows and pre-3.1 grid responses can omit it
   * without a wider refactor of the heatmap mapper.
   */
  isArchived: z.boolean().optional(),
  /**
   * Phase 4 W5.T5.5 (Q10=a / M-6). Client name surfaced on the project
   * row label so an Employee viewing /workload sees «<projectName> ·
   * <clientName>» — the only place an Employee can read client info
   * (no /clients access). Optional because the BE may not always emit
   * it (older API images during a rollout) — when missing, the FE
   * falls back to project name only. Phase 5+ widens this to all
   * actors per Q10.
   */
  clientName: z.string().optional(),
  /**
   * R6.6 — employee's joining date (ISO `YYYY-MM-DD`). Present on
   * employee-subject rows (heatmap subject=employees). The FE compares
   * each `day.date` against this value to disable heatmap cells that fall
   * BEFORE the employee was hired (mirrors R6.4's top-level `hireDate` on
   * the per-employee grid response). Optional because:
   *   - Project-subject rows (per-employee calendar grid in M1) carry no
   *     hireDate at the row level — the response uses the top-level
   *     `hireDate` field instead.
   *   - Older BE images during a rollout may omit it; the FE falls back
   *     to "do not disable any cell" when missing.
   * Tooltip on disabled cells: «Сотрудник принят на работу с {DD.MM.YYYY}».
   */
  hireDate: WorkloadDateSchema.optional(),
  /**
   * Block 2 «Трекер объектов» Этап 01a (T2.2b). The «разделы» of THIS project
   * row, used by the FE to render section sub-rows when a project is expanded.
   *
   * The set is a UNION (B1 invariant):
   *   union(sections the SUBJECT is assigned to on this project [non-archived]
   *         ∪ sections that appear in this project's period FACTS [incl. any
   *           since-archived section — a fact on it must never orphan]).
   * This guarantees «collapsed = Σ(expanded)»: a fact logged on a section the
   * subject is no longer (or was never) assigned to still surfaces as a sub-row
   * (it is in the fact-set arm), and an assigned section with 0 hours in the
   * period still shows (variant a).
   *
   * Второй arm нужен и после ужесточения гейта ЗАПИСИ (05.08.2026, часы можно
   * записать только по назначенному разделу): назначение снимается во времени,
   * а уже записанные часы решением сохраняются читаемыми. Чтение шире записи
   * намеренно.
   *
   * «Прочее» (NULL-section facts) is NOT a member here — it has no section id;
   * the FE renders it from `cells[].sectionId === null`. `row.id` stays the
   * projectId (collapsed view unchanged) and `cells[]` stays a FLAT array
   * carrying `sectionId`; sub-rows are derived by the FE, never via a composite
   * `row.id`.
   *
   * Strictly additive + `.optional()`: heatmap rows, project rows with no
   * sections, and pre-phase API images omit it; the FE treats missing/empty as
   * "no section sub-rows" and renders the project exactly as today.
   */
  sections: z.array(WorkloadGridSectionSchema).optional(),
});
export type WorkloadGridRow = z.infer<typeof WorkloadGridRowSchema>;

/**
 * Р-52 — РАБОТА СУБЪЕКТА, ЧЕЙ ПЕРИОД ЗАДЕВАЕТ ОКНО КАЛЕНДАРЯ.
 *
 * Один плоский массив кормит ОБА требования решения: пометку «в этот день у
 * человека есть работа» в клетке и состав дня в окне по нажатию. Форма выбрана
 * так, а не «пометка на пересечении строки и дня» плюс отдельное чтение состава,
 * по двум причинам сразу:
 *
 *   1. РАЗМЕР. Массив имеет длину «число работ в окне» (единицы-десятки), а не
 *      «месяц × десятки объектов». Матрица пометок была бы на два порядка
 *      больше и при этом НЕ ответила бы на второй вопрос — какие именно работы;
 *   2. ОДИН ИСТОЧНИК ПРАВДЫ. Пометка и состав дня выводятся из одного и того
 *      же массива одной и той же функцией (`worksCoveringDay` на клиенте).
 *      Второе чтение «состав дня» завело бы вторую выборку с собственным
 *      предикатом перекрытия — и разъехались бы они ТИХО: клетка помечена, окно
 *      пусто, и наоборот.
 *
 * ⚠️ ОЦЕНКИ ЧАСОВ ЗДЕСЬ НЕТ И БЫТЬ НЕ МОЖЕТ. `Work.hoursEstimate` лежит в
 * `SENSITIVE_FIELDS` (`common/audit/redact.ts`) и маскируется даже в снимке
 * аудита; календарь — управленческая поверхность, и поле, положенное сюда,
 * обошло бы маскирование целиком. Критерий 4 решения Р-52 проверяет ОТВЕТ
 * СЕРВЕРА, а не экран: «в клетке числа нет» зелено и у реализации, которая
 * поле прислала и не нарисовала. Добавление сюда любого поля с часами —
 * нарушение границы решения, а не расширение договора.
 *
 * ⚠️ ЗЕРКАЛО `GanttBarSchema` МИНУС `projectName` И `masked`, и расхождение
 * намеренное. Имя объекта клиенту здесь не нужно — оно уже стоит в
 * `rows[].label` той же выдачи, и второй экземпляр имени разошёлся бы с первым.
 * Маскирования нет, потому что рубеж видимости у сетки ДРУГОЙ: активный объект
 * субъекта сетка показывает безусловно (см. `getGrid`), и полосы ганта,
 * вычищенные `projectVisibilityWhere`, отняли бы пометку у строки, которую
 * сетка законно показывает.
 */
export const WorkloadGridWorkSchema = z.object({
  id: UuidSchema,
  kind: z.enum(['PROJECT_WORK', 'TASK']),
  /**
   * ⚠️ ОБНУЛЯЕМ, И ЭТО НЕ ФОРМАЛЬНОСТЬ. `Work.projectId` nullable у задач.
   * Строки календарной сетки — ОБЪЕКТЫ, поэтому работе без объекта строки не
   * достаётся и пометке взяться не на чем (граница Р-52, названная в самом
   * решении). Такая работа ВСЁ РАВНО едет по проводу: потерять её на сервере
   * значило бы сделать потерю неотличимой от отсутствия, а так она видна в
   * ответе и запинена проверкой.
   */
  projectId: UuidSchema.nullable(),
  sectionId: UuidSchema.nullable(),
  sectionName: z.string().nullable(),
  /** Пустое название — ЗАКОННОЕ состояние (`work.ts`): экран показывает имя раздела. */
  title: z.string().nullable(),
  startDate: WorkloadDateSchema,
  dueDate: WorkloadDateSchema,
  status: z.enum(['NOT_STARTED', 'IN_PROGRESS', 'ON_REVIEW', 'DONE']),
  /**
   * Р-50 — день ЗАКРЫТИЯ. Правый край работы у завершённой берётся отсюда, а не
   * из `dueDate`, и клиент считает его ОДНОЙ функцией с графиком занятости
   * (`barEndDate`, `components/gantt/gantt-lib.ts`). Второй реализации того же
   * правила здесь не заводится: разойдясь, две поверхности одного плана
   * показали бы разную длительность одной работы на одном экране.
   *
   * Календарный день, а не отметка времени: перевод делает сервер по канону
   * `Europe/Moscow` (`toMskDate`), потому что клиентский перевод разошёлся бы с
   * серверным на сутки для всего, что закрыто поздним вечером.
   */
  completedDate: WorkloadDateSchema.nullable(),
});
export type WorkloadGridWork = z.infer<typeof WorkloadGridWorkSchema>;

/**
 * `GET /workload/grid` response. Includes the request-scoped `today_msk`
 * (OQ-W9) so all client-side boundary logic uses the SAME day the server
 * just used — eliminates JS `new Date()` drift across midnight.
 */
export const WorkloadGridResponseSchema = z.object({
  days: z.array(WorkloadGridDaySchema),
  rows: z.array(WorkloadGridRowSchema),
  cells: z.array(WorkloadCellResponseSchema),
  today_msk: WorkloadDateSchema,
  /**
   * R6.4 — subject employee's joining date (ISO `YYYY-MM-DD`). Always
   * present for the employee-subject grid (the only subject in M1 per
   * D09). The FE compares each `day.date` against this value to disable
   * calendar cells that fall BEFORE the employee was hired. Tooltip:
   * «Сотрудник принят на работу с {DD.MM.YYYY}».
   */
  hireDate: WorkloadDateSchema,
  /**
   * v3 — active project ids the SUBJECT employee "owns": ProjectParticipant
   * ∪ РО (ProjectManager.roUserId) ∪ ГИП (Project.gipId). Active only
   * (archivedAt: null) — archived ids never appear. UNFILTERED by the grid's
   * ?projectId= (covers every owned project even when the calendar is
   * project-filtered). Drives the fact-modal «Мои проекты» vs «Другие
   * проекты» split. Optional for forward-compat with stale API images: when
   * undefined the FE fails closed (every active project → «Другие», «Мои» empty).
   */
  myProjectIds: z.array(z.string()).optional(),
  /**
   * Р-52 — работы субъекта, чьи периоды задевают окно. Источник И пометки в
   * клетке, И состава дня в окне (разбор — у `WorkloadGridWorkSchema`).
   *
   * `.optional()` по той же причине, что и `myProjectIds`: во время выката в
   * кэше клиента лежит ответ СТАРОГО сервера, где поля нет вовсе. Отсутствие
   * читается как «работ нет» — то есть пометок нет и клетка ведёт себя как до
   * волны. Это отказ в безопасную сторону: лишней пометки не появляется.
   */
  works: z.array(WorkloadGridWorkSchema).optional(),
  /** Aggregate response version — bumped on any mutation in scope. */
  version: z.number().int().nonnegative(),
});
export type WorkloadGridResponse = z.infer<typeof WorkloadGridResponseSchema>;

/**
 * Heatmap bucket index per D07: 0 = no hours, 1 = 1-3, 2 = 4-5,
 * 3 = 6-7, 4 = 8 (на план), 5 = > 8 (overload).
 */
export const WorkloadHeatmapBucketSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
]);
export type WorkloadHeatmapBucket = z.infer<typeof WorkloadHeatmapBucketSchema>;

/** A heat cell — single hours figure + bucket + absence flag. */
export const WorkloadHeatmapCellSchema = z.object({
  date: WorkloadDateSchema,
  rowId: UuidSchema,
  /**
   * Часы ЗАГРУЗКИ — то, чем карта красит клетку и считает «Итого».
   *
   * ⚠️ В ДЕНЬ ОТСУТСТВИЯ ЗДЕСЬ НОЛЬ, И ЭТО ПРАВИЛО D11, А НЕ ПОТЕРЯ. День
   * отпуска не нагрузка, поэтому он не красится и в сумму периода не входит.
   * Настоящая сумма отчётов того же дня живёт в {@link factHours} рядом.
   */
  hours: z.number(),
  /**
   * Р-56 (дополнение 20.08.2026) — СУММА ФАКТИЧЕСКИХ ОТЧЁТОВ ДНЯ, БЕЗ ОГЛЯДКИ
   * НА ОТСУТСТВИЕ.
   *
   * ⚠️ ПОЛЕ ДОБАВЛЕНО, А `hours` НЕ ТРОНУТО, И ЭТО ГРАНИЦА. Решение заказчика
   * требует показать в режиме «Все сотрудники» значок отсутствия И ЧАСЫ РЯДОМ
   * — иначе на одном дне выходило три ответа: руководителю прочерк (карта
   * обнулила), окну той же клетки «Итого 4» (оно считает отчёты), а самому
   * сотруднику 4 (его ветка считает свою сетку). Поправить `hours` было бы
   * дешевле на одну строку и НЕВЕРНО: то же поле красит клетки самой тепловой
   * карты и складывается в её «Итого», то есть волна поменяла бы поверхность
   * Ф3, о которой её решение молчит.
   *
   * Необязательное: устаревший сервер поля не пришлёт, и клиент обязан падать
   * на `hours` — тогда в день отсутствия он снова покажет ноль, то есть
   * ПРЕЖНЕЕ поведение, а не пустой экран.
   */
  factHours: z.number().optional(),
  bucket: WorkloadHeatmapBucketSchema,
  isAbsence: z.boolean(),
  absenceKind: z.enum(['VACATION', 'SICK_LEAVE']).nullable().optional(),
});
export type WorkloadHeatmapCell = z.infer<typeof WorkloadHeatmapCellSchema>;

export const WorkloadHeatmapResponseSchema = z.object({
  days: z.array(WorkloadGridDaySchema),
  rows: z.array(WorkloadGridRowSchema),
  /** Pre-bucketed cells — FE renders without recomputing. */
  buckets: z.array(WorkloadHeatmapCellSchema),
  today_msk: WorkloadDateSchema,
});
export type WorkloadHeatmapResponse = z.infer<typeof WorkloadHeatmapResponseSchema>;

/** A single audit-log entry rendered for the cell history panel. */
export const WorkloadChangelogEntrySchema = z.object({
  id: UuidSchema,
  at: z.string(),
  actorId: UuidSchema,
  actorName: z.string(),
  actorAccessLevel: z.enum(['EMPLOYEE', 'MANAGER', 'ADMIN', 'SUPERVISOR']),
  action: z.enum(['INSERT', 'UPDATE', 'DELETE']),
  entityKind: WorkloadEntityKindSchema,
  entityId: UuidSchema,
  /** JSON-patch-ish diff payload (server-shaped; opaque to FE). */
  diff: z.record(z.unknown()).nullable(),
});
export type WorkloadChangelogEntry = z.infer<typeof WorkloadChangelogEntrySchema>;

/**
 * Работа, названная в разнице, — В ТОЙ ФОРМЕ, ИЗ КОТОРОЙ ЭКРАН СОБИРАЕТ ПОДПИСЬ.
 *
 * ⚠️ ЗДЕСЬ НЕ ГОТОВАЯ СТРОКА, И ЭТО РЕШЕНИЕ. Правило подписи работы уже
 * записано ОДИН раз — `workPickerLabel` в вебе («название, при пустом —
 * имя раздела, при пустом обоих — период»). Отдай сервер готовую подпись, и
 * правило получило бы второй экземпляр: пикер и история назвали бы одну и ту же
 * работу по-разному ровно тогда, когда название пусто, — то есть в самом частом
 * случае (проектную работу заводят «на раздел», и названия у неё нет).
 */
export const WorkloadChangelogWorkRefSchema = z.object({
  title: z.string().nullable(),
  sectionName: z.string().nullable(),
  startDate: WorkloadDateSchema,
  dueDate: WorkloadDateSchema,
});
export type WorkloadChangelogWorkRef = z.infer<typeof WorkloadChangelogWorkRefSchema>;

export const WorkloadCellChangelogResponseSchema = z.object({
  rows: z.array(WorkloadChangelogEntrySchema),
  nextCursor: z.string().nullable(),
  /**
   * Имена для идентификаторов, встречающихся в `diff`, — Ф5 · В9.7, фикс-пак
   * круга (находка В-7).
   *
   * ⚠️ РАЗРЕШАЕТ СЕРВЕР, А НЕ ЭКРАН, и это несущее. Панель истории показывает
   * только те поля, для которых умеет показать ЗНАЧЕНИЕ по-человечески; раздел
   * и работа приходят идентификаторами, а своего справочника у панели нет.
   * Разрешатель на стороне экрана (по спискам, которые окно и так загрузило)
   * промахивался бы ровно в главном случае находки: раздел, с которого час
   * ПЕРЕВЕСИЛИ, из союза «назначенные ∪ разделы фактов» выпадает, как только на
   * него не ссылается ни один факт. Сервер же разрешает по идентификатору
   * прямо из базы и промахнуться не может.
   *
   * ⚠️ АРХИВНЫЕ И СНЯТЫЕ СТРОКИ ТОЖЕ РАЗРЕШАЮТСЯ: история говорит о ПРОШЛЫХ
   * состояниях, и «работа, которую с тех пор сняли» обязана называться, а не
   * исчезать.
   *
   * `.default({})` — чтобы старая запись ответа (фикстуры, кеш клиента) читалась
   * без правки, а тип на обеих сторонах оставался необязательным-свободным.
   */
  sections: z.record(z.string()).default({}),
  works: z.record(WorkloadChangelogWorkRefSchema).default({}),
});
export type WorkloadCellChangelogResponse = z.infer<typeof WorkloadCellChangelogResponseSchema>;

// ─── График занятости (Гант) — Блок 2 · Ф2 · В2a ──────────────────────────
//
// `GET /workload/gantt`. Третий отчёт того же контура, что сетка и тепловая
// карта, поэтому контракт живёт здесь, рядом с ними, а не в `work.ts`: искать
// его будут по адресу маршрута.
//
// ─── ЧЕГО В КОНТРАКТЕ НЕТ И ПОЧЕМУ ─────────────────────────────────────────
//
// **Часов нет ни в каком виде.** `Work.hoursEstimate` маскируется даже в
// снимке аудита; отчёт, отдающий её сумму, обошёл бы маскирование арифметикой.
//
// **`undated[]` нет — плана здесь не хватило схемы.** План описывает ответ как
// `{ rows: [{ …, bars[], absences[], undated[] }] }`, но `Work.startDate` и
// `Work.dueDate` — NOT NULL (миграция 046), то есть работы без периода не
// бывает и массив пуст ПО ПОСТРОЕНИЮ. Отдавать его значило бы попросить В4
// нарисовать ветку интерфейса, которая не исполнится никогда. Расхождение
// названо в отчёте волны.
//
// **Фильтра по виду работы нет, и это решение заказчика 06.08.2026:** на
// график попадают И проектные работы, И задачи. Прежняя формулировка «Гант по
// назначениям» отменена — по её букве выборка ушла бы в `kind='PROJECT_WORK'`.

/** Полоса графика. Имена пусты у задачи без проекта — это ЗАКОННОЕ состояние. */
export const GanttBarSchema = z.object({
  workId: UuidSchema,
  kind: z.enum(['PROJECT_WORK', 'TASK']),
  /**
   * У задачи проект и раздел могут отсутствовать (`Work.projectId` nullable +
   * CHECK по виду). Полоса тогда несёт только название — выбрасывать её нельзя,
   * иначе непроектное время человека исчезает с графика целиком.
   */
  projectId: UuidSchema.nullable(),
  projectName: z.string().nullable(),
  sectionId: UuidSchema.nullable(),
  sectionName: z.string().nullable(),
  title: z.string().nullable(),
  startDate: WorkloadDateSchema,
  dueDate: WorkloadDateSchema,
  status: z.enum(['NOT_STARTED', 'IN_PROGRESS', 'ON_REVIEW', 'DONE']),
  /**
   * Р-50 — день ЗАКРЫТИЯ работы; правый край полосы у завершённой берётся
   * отсюда, а не из `dueDate`. Полоса до планового срока занимала дни, в
   * которые работы заведомо уже не было.
   *
   * ⚠️ КАЛЕНДАРНЫЙ ДЕНЬ, А НЕ ОТМЕТКА ВРЕМЕНИ, И ЭТО НЕСУЩЕЕ. В базе лежит
   * `Work.completedAt` — timestamp; перевод его в день зависит от часового
   * пояса, и сделанный на клиенте разошёлся бы с сервером на сутки для всего,
   * что закрыто поздним вечером. Перевод делает СЕРВЕР по канону проекта
   * (`Europe/Moscow`), клиент получает готовый день той же формы, что
   * `startDate` и `dueDate`.
   *
   * ⚠️ ОБНУЛЯЕМОЕ — И РУБЕЖ ПРОХОДИТ ИМЕННО ЗДЕСЬ, А НЕ В БАЗЕ. Двусторонний
   * CHECK `work_done_completed_at` держит «готово ⟺ дата закрытия есть», то
   * есть строки «готово без даты» в базе не существует и завести её нельзя.
   * Но сервер вправе не положить поле в выборку — значит клиент обязан вести
   * себя ОПРЕДЕЛЁННО: пустое поле означает «край на сроке», а не «полосы
   * нет». Проверяется фикстурой договора; тот же разбор — в поправке к Р-30.
   *
   * У незавершённой работы поле пусто по построению и на показ не влияет.
   */
  completedDate: WorkloadDateSchema.nullable(),
  /**
   * `true` — проект полосы актору невидим, имена вычищены. К задаче БЕЗ
   * проекта маскирование неприменимо вовсе (маскировать нечего), и её
   * видимость держит ОДНА когорта: видишь человека — видишь его задачи.
   */
  masked: z.boolean(),
});
export type GanttBar = z.infer<typeof GanttBarSchema>;

/** Отсутствие — ОТДЕЛЬНАЯ полоса, а не фон полосы работы (В4, F2.3). */
export const GanttAbsenceSchema = z.object({
  id: UuidSchema,
  type: z.enum(['VACATION', 'SICK_LEAVE']),
  dateFrom: WorkloadDateSchema,
  dateTo: WorkloadDateSchema,
});
export type GanttAbsence = z.infer<typeof GanttAbsenceSchema>;

export const GanttRowSchema = z.object({
  userId: UuidSchema,
  fullName: z.string(),
  departmentId: z.number().int().nullable(),
  bars: z.array(GanttBarSchema),
  absences: z.array(GanttAbsenceSchema),
});
export type GanttRow = z.infer<typeof GanttRowSchema>;

export const WorkloadGanttQuerySchema = z
  .object({
    periodFrom: WorkloadDateSchema,
    periodTo: WorkloadDateSchema,
    departmentId: z.coerce.number().int().positive().optional(),
    projectId: UuidSchema.optional(),
    userId: UuidSchema.optional(),
    sectionId: UuidSchema.optional(),
    /**
     * ОСЬ ВИДА РАБОТЫ — вторая ось отбора графика (Ф4 · В-ПР, Р-33).
     * «Все» выражается ОТСУТСТВИЕМ параметра, а не третьим значением: умолчание
     * графика — оба вида разом (решение заказчика 06.08.2026), и заводить под
     * него литерал значило бы завести второй способ сказать то же самое.
     *
     * ⚠️ ОСЬ ЖИВЁТ ТОЛЬКО ЗДЕСЬ. На «Мою работу» и «Поручения» она НЕ
     * переносится (Р-33, прямая граница): там вид уже выражен устройством
     * экрана — вкладками (Р-28) и единственным содержимым (Р-29), — и второй
     * орган управления тем же признаком дал бы два источника одного состояния.
     *
     * ⚠️ В СЧЁТЧИКИ СКРЫТОГО НЕ ВХОДИТ — см. «ЧТО В ЧИСЛА НЕ ВХОДИТ» у
     * {@link GanttHiddenSchema}, пункт 4. Довод там же, и он не про экономию.
     */
    kind: z.enum(['PROJECT_WORK', 'TASK']).optional(),
    /**
     * Срочные. Разворачивается в предикат `dueDate <= today + 1` — колонки
     * срочности нет и не будет, срочность ВЫВОДИТСЯ из срока. `today` при этом
     * приходит из общего серверного источника, а не из часового пояса клиента.
     */
    dueSoon: z.enum(['true', 'false']).optional(),
  })
  .strict();
export type WorkloadGanttQuery = z.infer<typeof WorkloadGanttQuerySchema>;

/**
 * Сколько отсечено ФИЛЬТРОМ по объекту или разделу — Ф3 · В4, требование C-2.
 *
 * ─── ПОЧЕМУ ЧИСЛА ДВА, А НЕ ОДНО ───────────────────────────────────────────
 *
 * Скрытого два вида, и путать их нельзя. Фильтр по объекту сужает не только
 * полосы, но и САМУ КОГОРТУ (`workload-cohort.ts`, ветка `scope.projectId` —
 * с 19.08.2026 это участник ИЛИ исполнитель живой работы объекта, а не одно
 * лишь участие): человек, никак с объектом не связанный, исчезает СТРОКОЙ
 * ЦЕЛИКОМ.
 * Одно число «скрыто 12» на экране, где пропало и три полосы, и девять строк,
 * не значит ничего.
 *
 * ─── ПОЧЕМУ ЭТО ПОЛЕ ОТВЕТА, А НЕ РАСЧЁТ КЛИЕНТА ───────────────────────────
 *
 * Клиент не видит того, чего ему не прислали: отсечённых строк в ответе нет
 * ПО ПОСТРОЕНИЮ. Критерий «на экране есть признак скрытого», закрытый на
 * стороне клиента, закрывался бы захардкоженным числом — и выглядел бы
 * исправным.
 *
 * ─── ЧТО В ЧИСЛА НЕ ВХОДИТ ─────────────────────────────────────────────────
 *
 * 1. `userId` — он выражает намерение «покажи одного», и «скрыто 39 строк» на
 *    нём было бы шумом, а не предупреждением.
 * 2. `dueSoon` — то же самое намерение («покажи срочные»), и оно НЕ теряет
 *    данные молча: пользователь сам его включил и видит его состояние.
 * 3. МАСКИРОВАННЫЕ полосы. Они не скрыты — они показаны, просто без имён.
 * 4. `kind` — ось вида работы (Ф4 · В-ПР, Р-33). Тот же класс, что `userId` и
 *    `dueSoon`: фильтр НАМЕРЕНИЯ, состояние которого пользователь видит на
 *    экране. ⚠️ Записано явно потому, что довод «он тоже убирает полосы, значит
 *    их надо посчитать» ВЕРЕН по букве и приводит к обратному выводу у того, кто
 *    прецедента `dueSoon` не прочтёт. Механика этого не оставлена на
 *    дисциплину: `kind` кладётся в `barScopeWhere` — то есть в предикат, ОБЩИЙ
 *    для показанной выборки и для базы счёта, — и потому сокращает оба
 *    слагаемых разности. Положенный в фильтры `findMany` рядом с `projectId`,
 *    он сузил бы только показанное, и «скрыто 137 полос» предупреждало бы
 *    человека о том, что он попросил сам.
 *
 * `bars` считается по СТРОКАМ, КОТОРЫЕ ОСТАЛИСЬ НА ЭКРАНЕ. Полосы исчезнувших
 * строк не входят ни сюда, ни куда-либо ещё — иначе одна и та же потеря была бы
 * посчитана дважды, и сумма перестала бы читаться.
 */
export const GanttHiddenSchema = z.object({
  /** Полос отсечено у показанных строк. */
  bars: z.number().int().nonnegative(),
  /** Строк отсечено целиком (человек выпал из когорты). */
  rows: z.number().int().nonnegative(),
});
export type GanttHidden = z.infer<typeof GanttHiddenSchema>;

export const WorkloadGanttResponseSchema = z.object({
  rows: z.array(GanttRowSchema),
  /** Серверное «сегодня» (`Europe/Moscow`) — им же клиент считает срочность. */
  today: WorkloadDateSchema,
  /**
   * Отсечённое фильтром по объекту/разделу. Оба нуля — фильтр не активен.
   *
   * ⚠️ ПОЛЕ НЕОБЯЗАТЕЛЬНОЕ С БЕЗОПАСНЫМ УМОЛЧАНИЕМ, И ЭТО НЕ ПОСЛАБЛЕНИЕ.
   * Обязательным оно делало выкат УПОРЯДОЧЕННЫМ: веб, уехавший на стенд раньше
   * сервера, получил бы ответ без поля, споткнулся на типе и уронил вкладку
   * ЦЕЛИКОМ. Порядок выката соблюдается человеком и потому однажды не
   * соблюдается — лечится это в коде, а не регламентом.
   *
   * Умолчание безопасно ИМЕННО нулями: «ничего не скрыто» — то же, что видит
   * пользователь без фильтра. Худший исход устаревшего сервера — ОТСУТСТВИЕ
   * предупреждения, а не ложное предупреждение и не пустой экран.
   */
  hidden: GanttHiddenSchema.optional().default({ bars: 0, rows: 0 }),
});
export type WorkloadGanttResponse = z.infer<typeof WorkloadGanttResponseSchema>;
